import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from 'react'
import type {
  PanelConfig,
  PanelRegistration,
  PanelState,
  PanelData,
  ShojiContextValue,
} from './types'
import { PANEL_REGISTRY_IMPORTS } from './types'
import { ANIMATION_DURATION } from './constants'

const ShojiContext = createContext<ShojiContextValue | null>(null)

// Global panel counter for unique IDs
let panelCounter = 0

interface ShojiProviderProps {
  children: ReactNode
  initialPanels?: PanelConfig[]
}

export function ShojiProvider({ children, initialPanels = [] }: ShojiProviderProps) {
  // Initialize panels with default state if not provided
  const normalizedInitialPanels = initialPanels.map(panel => ({
    ...panel,
    state: panel.state || 'expanded' as PanelState
  }))
  
  const [panels, setPanels] = useState<PanelConfig[]>(normalizedInitialPanels)
  const [animatingPanels, setAnimatingPanels] = useState<Set<string>>(new Set())
  const [transitioningPanels, setTransitioningPanels] = useState<Record<string, { from: PanelState; to: PanelState }>>({})
  const prevPanelStatesRef = useRef<Record<string, PanelState>>({})
  const panelDimensionsRef = useRef<Record<string, {width: number, height: number}>>({})
  const panelRefsRef = useRef<Record<string, HTMLElement>>({})
  const animationTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({})
  
  // Initialize previous states tracking
  useEffect(() => {
    normalizedInitialPanels.forEach(panel => {
      prevPanelStatesRef.current[panel.id] = panel.state || 'expanded'
    })
  }, [])
  const [panelRegistry, setPanelRegistry] = useState<Record<string, PanelRegistration>>({})
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 768 : true)
  const [isMapFocusMode, setIsMapFocusMode] = useState(false)
  const [focusedPanelId, setFocusedPanelId] = useState<string | null>(null)
  // Track panel focus order - most recently focused panels have higher z-index
  const [focusStack, setFocusStack] = useState<string[]>([])

  // Auto-register known panel types on initialization
  useEffect(() => {
    const registerDefaultPanels = async () => {
      try {
        // Register panels based on centralized registry
        const registrationPromises = Object.entries(PANEL_REGISTRY_IMPORTS).map(
          async ([type, importFn]) => {
            try {
              const registration = await importFn()
              setPanelRegistry(prev => ({ ...prev, [type]: registration }))
            } catch (error) {
              console.warn(`Failed to register ${type} panel:`, error)
            }
          }
        )
        
        await Promise.all(registrationPromises)
      } catch (error) {
        console.warn('Failed to register panels:', error)
      }
    }

    registerDefaultPanels()
  }, [])

  // Responsive detection
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Define toggleMapFocus first
  const toggleMapFocus = useCallback(() => {
    setIsMapFocusMode(prev => {
      const newMapFocus = !prev
      // Use a separate state update to avoid circular dependency
      setTimeout(() => {
        if (newMapFocus) {
          // When entering map focus, collapse all panels to slim
          setPanels(prev => prev.map(panel => ({ ...panel, state: 'slim' as PanelState })))
        } else {
          // When exiting map focus, restore panels to expanded state
          setPanels(prev => prev.map(panel => ({ ...panel, state: 'expanded' as PanelState })))
        }
      }, 0)
      return newMapFocus
    })
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Toggle map focus with 'M' key (only if not typing in input/textarea)
      if (event.key === 'M' || event.key === 'm') {
        const target = event.target as HTMLElement
        const isTyping = target.tagName === 'INPUT' || 
                        target.tagName === 'TEXTAREA' || 
                        target.contentEditable === 'true'
        
        if (!isTyping) {
          event.preventDefault()
          toggleMapFocus()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleMapFocus])

  const addPanel = useCallback((config: Omit<PanelConfig, 'id'>) => {
    const id = `panel-${++panelCounter}`
    const newPanel: PanelConfig = { 
      ...config, 
      id,
      state: config.state || 'expanded'
    }
    
    setPanels(prev => [...prev, newPanel])
    
    // Auto-focus the newly added panel and update focus stack
    setFocusedPanelId(id)
    setFocusStack(prev => {
      // Remove panel if it's already in stack, then add to front
      const filtered = prev.filter(stackId => stackId !== id)
      return [id, ...filtered]
    })
    
    return id
  }, [])

  const removePanel = useCallback((id: string) => {
    setPanels(prev => {
      const newPanels = prev.filter(panel => panel.id !== id)
      
      // Clean up focus stack
      setFocusStack(prevStack => prevStack.filter(stackId => stackId !== id))
      
      // If we removed the focused panel, focus the rightmost remaining panel
      if (focusedPanelId === id && newPanels.length > 0) {
        const rightmostId = newPanels[newPanels.length - 1].id
        setFocusedPanelId(rightmostId)
        setFocusStack(prevStack => {
          const filtered = prevStack.filter(stackId => stackId !== rightmostId)
          return [rightmostId, ...filtered]
        })
      }
      // If no panels remain, clear focus
      else if (newPanels.length === 0) {
        setFocusedPanelId(null)
        setFocusStack([])
      }
      
      return newPanels
    })
  }, [focusedPanelId])

  const removePanelsAfter = useCallback((anchorId: string) => {
    setPanels(prev => {
      const anchorIndex = prev.findIndex(panel => panel.id === anchorId)
      if (anchorIndex === -1) return prev
      
      const newPanels = prev.slice(0, anchorIndex + 1)
      const removedPanelIds = prev.slice(anchorIndex + 1).map(p => p.id)
      
      // Clean up focus stack for removed panels
      setFocusStack(prevStack => prevStack.filter(id => !removedPanelIds.includes(id)))
      
      // If focused panel was removed, focus the anchor panel
      if (removedPanelIds.includes(focusedPanelId!)) {
        setFocusedPanelId(anchorId)
        setFocusStack(prevStack => {
          const filtered = prevStack.filter(id => id !== anchorId)
          return [anchorId, ...filtered]
        })
      }
      
      return newPanels
    })
  }, [focusedPanelId])

  const updatePanelData = useCallback((id: string, data: PanelData) => {
    setPanels(prev => prev.map(panel => 
      panel.id === id ? { ...panel, data } : panel
    ))
  }, [])

  const registerPanelRef = useCallback((id: string, ref: HTMLElement | null) => {
    if (ref) {
      panelRefsRef.current[id] = ref
    } else {
      delete panelRefsRef.current[id]
    }
  }, [])

  // Clean up animation timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(animationTimeoutsRef.current).forEach(clearTimeout)
    }
  }, [])

  // Animation completion handler
  const handleAnimationComplete = useCallback((id: string) => {
    setAnimatingPanels(prev => {
      const newSet = new Set(prev)
      newSet.delete(id)
      return newSet
    })
    
    // Clear any pending timeout for this panel
    if (animationTimeoutsRef.current[id]) {
      clearTimeout(animationTimeoutsRef.current[id])
      delete animationTimeoutsRef.current[id]
    }
  }, [])

  const updatePanelState = useCallback((id: string, state: PanelState) => {
    // Check if this is a state change that should trigger animation
    const prevState = prevPanelStatesRef.current[id]
    if (prevState && prevState !== state) {
      // Capture current panel dimensions before animation starts using registered ref
      const panelElement = panelRefsRef.current[id]
      if (panelElement) {
        const bounds = panelElement.getBoundingClientRect()
        panelDimensionsRef.current[id] = { width: bounds.width, height: bounds.height }
      }
      
      // Check if this is a layout-changing transition (e.g., to/from widget state)
      const isLayoutTransition = 
        (prevState !== 'widget' && state === 'widget') ||
        (prevState === 'widget' && state !== 'widget')
      
      if (isLayoutTransition) {
        // For layout transitions, mark panel as transitioning but don't change state immediately
        setTransitioningPanels(prev => ({
          ...prev,
          [id]: { from: prevState, to: state }
        }))
        
        // Delay the actual state change to allow layout animation to start smoothly
        setTimeout(() => {
          setPanels(prev => {
            const updated = prev.map(panel => 
              panel.id === id ? { ...panel, state } : panel
            )
            return updated
          })
          
          // Clear transitioning state after a brief delay
          setTimeout(() => {
            setTransitioningPanels(prev => {
              const { [id]: removed, ...rest } = prev
              return rest
            })
          }, 50) // Small delay to ensure smooth transition
        }, 16) // Single frame delay
      } else {
        // For non-layout transitions, update immediately
        setPanels(prev => {
          const updated = prev.map(panel => 
            panel.id === id ? { ...panel, state } : panel
          )
          return updated
        })
      }
      
      setAnimatingPanels(prev => new Set([...prev, id]))
      
      // Clear any existing timeout for this panel
      if (animationTimeoutsRef.current[id]) {
        clearTimeout(animationTimeoutsRef.current[id])
      }
      
      // Set fallback timeout in case onAnimationComplete doesn't fire
      // Use longer duration for layout transitions (minimize/maximize)
      const timeoutDuration = isLayoutTransition 
        ? Math.max(ANIMATION_DURATION.MINIMIZE_ANIMATION, ANIMATION_DURATION.MAXIMIZE_ANIMATION)
        : ANIMATION_DURATION.PANEL_STATE_TRANSITION
      
      animationTimeoutsRef.current[id] = setTimeout(() => {
        handleAnimationComplete(id)
      }, timeoutDuration)
      
      // Update the previous state tracking
      prevPanelStatesRef.current[id] = state
    }
  }, [])

  const expandPanel = useCallback((id: string) => {
    updatePanelState(id, 'expanded')
  }, [updatePanelState])

  const collapseToWidget = useCallback((id: string) => {
    updatePanelState(id, 'widget')
  }, [updatePanelState])

  const collapseToSlim = useCallback((id: string) => {
    updatePanelState(id, 'slim')
  }, [updatePanelState])

  const registerPanelType = useCallback((type: string, registration: PanelRegistration) => {
    setPanelRegistry(prev => ({ ...prev, [type]: registration }))
  }, [])

  const getPanelComponent = useCallback((type: string) => {
    return panelRegistry[type]?.component || null
  }, [panelRegistry])

  const isLeftmostPanel = useCallback((panelId: string) => {
    return panels.length > 0 && panels[0].id === panelId
  }, [panels])

  const getPanelPrevDimensions = useCallback((id: string) => {
    return panelDimensionsRef.current[id] || null
  }, [])

  const focusPanel = useCallback((panelId: string) => {
    setFocusedPanelId(panelId)
    // Update focus stack - move this panel to the front
    setFocusStack(prev => {
      // Remove panel if it's already in stack, then add to front
      const filtered = prev.filter(id => id !== panelId)
      return [panelId, ...filtered]
    })
  }, [])

  // Simplified z-index calculation function
  const getPanelZIndex = useCallback((panelId: string): number => {
    const baseZIndex = 40
    
    if (focusedPanelId === panelId) {
      return baseZIndex + 1000 // Clear separation for focused panel
    }
    
    const focusIndex = focusStack.indexOf(panelId)
    if (focusIndex !== -1) {
      // Recently focused panels: higher index = lower z-index
      return baseZIndex + 100 + (focusStack.length - focusIndex)
    }
    
    // Never focused: use panel array order
    const panelIndex = panels.findIndex(p => p.id === panelId)
    return baseZIndex + panelIndex
  }, [focusedPanelId, focusStack, panels])

  const unfocusPanel = useCallback(() => {
    // Don't allow unfocus if there's only one panel
    if (panels.length === 1) {
      return
    }
    setFocusedPanelId(null)
  }, [panels.length])

  // Focus management: initial focus and single panel auto-focus
  useEffect(() => {
    if (panels.length === 0) {
      // No panels, no focus
      if (focusedPanelId !== null) {
        setFocusedPanelId(null)
        setFocusStack([])
      }
    } else if (panels.length === 1) {
      // Single panel should always have focus
      if (focusedPanelId !== panels[0].id) {
        setFocusedPanelId(panels[0].id)
        setFocusStack([panels[0].id])
      }
    } else if (focusedPanelId === null) {
      // Multiple panels but no focus - focus the rightmost panel
      const rightmostId = panels[panels.length - 1].id
      setFocusedPanelId(rightmostId)
      setFocusStack([rightmostId])
    } else {
      // Check if currently focused panel still exists
      const focusedPanelExists = panels.some(panel => panel.id === focusedPanelId)
      if (!focusedPanelExists) {
        // Focused panel was removed, focus the rightmost panel
        const rightmostId = panels[panels.length - 1].id
        setFocusedPanelId(rightmostId)
        setFocusStack([rightmostId])
      }
      // If the focused panel is the rightmost panel, don't override it
      // This prevents the effect from interfering with newly added panels
    }
  }, [panels, focusedPanelId])

  const value: ShojiContextValue = {
    panels,
    panelRegistry,
    isDesktop,
    isMapFocusMode,
    focusedPanelId,
    animatingPanels,
    transitioningPanels,
    getPanelPrevDimensions,
    registerPanelRef,
    handleAnimationComplete,
    addPanel,
    removePanel,
    removePanelsAfter,
    updatePanelData,
    updatePanelState,
    toggleMapFocus,
    expandPanel,
    collapseToWidget,
    collapseToSlim,
    registerPanelType,
    getPanelComponent,
    isLeftmostPanel,
    focusPanel,
    unfocusPanel,
    focusStack,
    getPanelZIndex,
  }

  return (
    <ShojiContext.Provider value={value}>
      {children}
    </ShojiContext.Provider>
  )
}

export function useShoji() {
  const context = useContext(ShojiContext)
  if (!context) {
    throw new Error('useShoji must be used within a ShojiProvider')
  }
  return context
}